import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Target, Plus, CheckCircle2, BookOpen, Brain, Timer, Trophy, Flame } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { WeeklyGoalsSetup } from './WeeklyGoalsSetup';
import { GoalProgressRing } from './GoalProgressRing';
import { startOfWeek, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { triggerConfetti, celebrateOnce } from '@/lib/confetti';

interface WeeklyGoal {
  id: string;
  goal_type: string;
  target_value: number;
  current_value: number;
  completed: boolean;
}

const goalTypeConfig: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  questions: { label: 'Questões', icon: <BookOpen className="h-4 w-4" />, color: 'hsl(var(--primary))' },
  correct_answers: { label: 'Acertos', icon: <CheckCircle2 className="h-4 w-4" />, color: 'hsl(142, 76%, 36%)' },
  simulations: { label: 'Simulados', icon: <Brain className="h-4 w-4" />, color: 'hsl(262, 83%, 58%)' },
  flashcards: { label: 'Flashcards', icon: <Target className="h-4 w-4" />, color: 'hsl(217, 91%, 60%)' },
  study_time: { label: 'Pomodoros', icon: <Timer className="h-4 w-4" />, color: 'hsl(38, 92%, 50%)' },
  streak: { label: 'Streak', icon: <Flame className="h-4 w-4" />, color: 'hsl(0, 84%, 60%)' },
};

export function WeeklyGoals() {
  const { user } = useAuth();
  const [goals, setGoals] = useState<WeeklyGoal[]>([]);
  const [loading, setLoading] = useState(true);
  const [setupOpen, setSetupOpen] = useState(false);
  // confetti now guarded by celebrateOnce(localStorage)
  
  const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
  const weekStartStr = format(weekStart, 'yyyy-MM-dd');

  const fetchGoals = async () => {
    if (!user) return;
    
    setLoading(true);
    const { data, error } = await supabase
      .from('user_weekly_goals')
      .select('*')
      .eq('user_id', user.id)
      .eq('week_start', weekStartStr);

    if (!error && data) {
      setGoals(data);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchGoals();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, weekStartStr]);

  // Check for all goals completed and trigger confetti
  const allGoalsCompleted = goals.length > 0 && goals.every(g => g.completed);
  
  useEffect(() => {
    if (allGoalsCompleted && user) {
      celebrateOnce(`weekly-goals:${user.id}:${weekStartStr}`, triggerConfetti);
    }
  }, [allGoalsCompleted, user, weekStartStr]);

  const totalProgress = goals.length > 0
    ? goals.reduce((sum, goal) => {
        const progress = Math.min(100, (goal.current_value / goal.target_value) * 100);
        return sum + progress;
      }, 0) / goals.length
    : 0;

  const completedGoals = goals.filter(g => g.completed).length;

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-muted rounded w-1/2" />
            <div className="h-20 bg-muted rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="bg-green-600 border-green-700 min-h-[260px] sm:min-h-[240px] h-full flex flex-col">
        <CardHeader className="pb-2">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <CardTitle className="text-lg flex items-center gap-2 text-white">
              <Trophy className="h-5 w-5 text-white" />
              Metas da Semana
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setSetupOpen(true)} className="text-white hover:bg-green-500 hover:text-white w-fit">
              <Plus className="h-4 w-4 mr-1" />
              {goals.length > 0 ? 'Editar' : 'Definir'}
            </Button>
          </div>
          <p className="text-sm text-green-100">
            Semana de {format(weekStart, "d 'de' MMMM", { locale: ptBR })}
          </p>
        </CardHeader>
        <CardContent className="flex-1 pt-0">
          {goals.length === 0 ? (
            <div className="text-center py-6">
              <Target className="h-12 w-12 text-green-200 mx-auto mb-3" />
              <p className="text-green-100 mb-4">
                Defina suas metas para esta semana
              </p>
              <Button onClick={() => setSetupOpen(true)} variant="secondary" className="bg-white text-green-700 hover:bg-green-50">
                <Plus className="h-4 w-4 mr-2" />
                Criar Metas
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Overall progress */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-green-100">Progresso geral</span>
                  <span className="font-medium text-white">{completedGoals}/{goals.length} completas</span>
                </div>
                <Progress value={totalProgress} className="h-2 bg-green-500 [&>div]:bg-white" />
              </div>

              {/* Individual goals */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-4">
                {goals.map((goal) => {
                  const config = goalTypeConfig[goal.goal_type] || {
                    label: goal.goal_type,
                    icon: <Target className="h-4 w-4" />,
                    color: 'white'
                  };
                  const progress = Math.min(100, (goal.current_value / goal.target_value) * 100);

                  return (
                    <div
                      key={goal.id}
                      className="flex flex-col items-center p-2 rounded-lg bg-green-500/50"
                    >
                      <GoalProgressRing
                        progress={progress}
                        size={60}
                        strokeWidth={6}
                        color="white"
                        completed={goal.completed}
                      />
                      <div className="mt-1 text-center">
                        <div className="flex items-center gap-1 justify-center text-xs font-medium text-white">
                          {config.icon}
                          {config.label}
                        </div>
                        <p className="text-xs text-green-100">
                          {goal.current_value}/{goal.target_value}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <WeeklyGoalsSetup
        open={setupOpen}
        onOpenChange={setSetupOpen}
        existingGoals={goals}
        weekStart={weekStartStr}
        onSave={fetchGoals}
      />
    </>
  );
}
